import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TrainingCertificate from '../components/TrainingCertificate';
import { formatStamp } from '../utils/datetime';
import '../styles/PeoplePanel.css';

/**
 * People — one row per person, across every training panel in the account.
 *
 * It has nothing to configure and nothing to build. Everything on it is aggregated from the
 * snapshots the training panels have already produced (`people_engine.merge`), which is why
 * it costs zero Orcanos calls and can be created in one click.
 *
 * Three things on this screen are load-bearing and easy to remove by accident:
 *
 *  1. **The coverage line.** An unbuilt training panel contributes nothing, and a roster that
 *     silently omits it looks exactly like a compliant one. The banner says how many panels
 *     were counted and names the ones that were not, so 100% is never printed over a panel
 *     that has simply never run.
 *  2. **"No training assigned" is not a pass.** Somebody whose job title no controlled
 *     document names has no obligations at all — 0 of 0 is drawn as its own grey state, never
 *     as a full bar. Showing it as compliance is the fabricated pass this module exists to
 *     prevent (Critical Notes #26, #29).
 *  3. **Overdue can be UNKNOWN.** When the snapshots' due dates read the same either way
 *     round the server sends `null`, not `0`, and this screen prints "—". "No overdue
 *     training" and "this panel cannot tell you" are opposite claims (Critical Note #45).
 *
 * Rows are searched, filtered and paged on the SERVER (Critical Note #38) — the rule lives in
 * `people_api._matches` and nowhere else.
 */

const PAGE_SIZES = [25, 50, 100];

const FILTERS = [
  { key: 'all', label: 'Everyone' },
  { key: 'outstanding', label: 'Outstanding' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'gaps', label: 'No task at all' },
  { key: 'compliant', label: 'Fully signed' },
  { key: 'none', label: 'No training assigned' },
];

const pctColor = (p) => (p < 50 ? 'var(--pp-miss-fg)' : p < 90 ? 'var(--pp-open-fg)' : 'var(--pp-ok-fg)');

const initials = (name, uid) => String(name || uid || '?')
  .replace(/[^\p{L}\p{N}\s.]/gu, ' ')
  .split(/[\s.]+/)
  .filter(Boolean)
  .map((w) => w[0])
  .join('')
  .slice(0, 2)
  .toUpperCase();

/* What each obligation state is called and coloured on the drawer. One place, so the drawer
   and the roster columns above it cannot drift into naming the same thing differently. */
const FILE_STATES = {
  done: { label: 'Signed', cls: 'pp-ok' },
  open: { label: 'Open', cls: 'pp-open' },
  draft: { label: 'Draft', cls: 'pp-na' },
  miss: { label: 'No task', cls: 'pp-miss' },
};

const FILE_VIEWS = [
  { key: 'all', label: 'Everything' },
  { key: 'done', label: 'Signed' },
  { key: 'open', label: 'Outstanding' },
  { key: 'miss', label: 'No task' },
];

/**
 * One person's training file.
 *
 * The drawer answers the question every roster row raises and none of them could: *which
 * ones?* It renders `person.docs` — the same de-duplicated obligations the row's counts are
 * derived from, so the two can never disagree.
 *
 * `only` filters the view, never the fetch: the header always states the person's real totals,
 * because a filtered denominator on a compliance screen is how "3 of 3" gets read as complete.
 */
function TrainingFile({ file, onClose, onCertificate, certBusy }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [view, setView] = useState(file.only || 'all');
  useEffect(() => { setView(file.only || 'all'); }, [file.uid, file.only]);

  const p = file.person;
  const docs = (p && p.docs) || [];
  const shown = docs.filter((d) => {
    const st = String(d.st || 'miss').toLowerCase();
    if (view === 'all') return true;
    if (view === 'open') return st === 'open' || st === 'draft';
    return st === view;
  });

  return (
    <>
      <div className="pp-drawer-backdrop" onClick={onClose} />
      <aside className="pp-drawer" role="dialog" aria-modal="true"
             aria-label={`Training file for ${file.name}`}>
        <div className="pp-drawer-bar">
          <div>
            <h3>{file.name}</h3>
            <p>{file.role || 'No job title on their User Profile'}</p>
          </div>
          <button className="pp-drawer-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        {file.loading && <div className="pp-drawer-msg">Reading this person’s file…</div>}
        {file.error && <div className="pp-error">{file.error}</div>}

        {p && (
          <div className="pp-drawer-body">
            <div className="pp-drawer-tiles">
              <div className="pp-dt"><b>{p.done}</b><span>Signed</span></div>
              <div className="pp-dt"><b>{p.open + p.draft}</b><span>Outstanding</span></div>
              <div className="pp-dt"><b>{p.miss}</b><span>No task</span></div>
              <div className="pp-dt">
                {/* `—`, never 0: "nothing overdue" and "the dates could not be read" are
                    opposite claims (Critical Note #45). */}
                <b>{p.overdue === null ? '—' : p.overdue}</b><span>Overdue</span>
              </div>
            </div>

            <div className="pp-drawer-note">
              {p.total} obligation{p.total === 1 ? '' : 's'} across{' '}
              {(p.panels || []).length || 1} panel{(p.panels || []).length === 1 ? '' : 's'}
              {file.builtAt && <> · oldest snapshot {formatStamp(file.builtAt)}</>}
              {(p.panels || []).length > 0 && <> · {p.panels.join(', ')}</>}
            </div>

            <div className="pp-drawer-views" role="group" aria-label="Filter this file">
              {FILE_VIEWS.map((v) => (
                <button key={v.key} type="button"
                        className={view === v.key ? 'active' : ''}
                        onClick={() => setView(v.key)}>{v.label}</button>
              ))}
            </div>

            {shown.length === 0 ? (
              <div className="pp-drawer-msg">
                {docs.length === 0
                  ? 'This person owes nothing — no controlled document names their job title.'
                  : 'Nothing in this person’s file matches that filter.'}
              </div>
            ) : (
              <table className="pp-table pp-file-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Rev</th>
                    <th>State</th>
                    <th>Signed</th>
                    <th>Due</th>
                    <th>From panel</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((d, i) => {
                    const st = FILE_STATES[String(d.st || 'miss').toLowerCase()]
                      || { label: d.st, cls: 'pp-na' };
                    return (
                      <tr key={`${d.k}-${d.rev}-${i}`}>
                        <td>
                          {/* The DOCUMENT, not the task: this column names the document, and
                              a link that opens something else is worse than none. `turl` is
                              the fallback only because an obligation with no task at all
                              (`miss`) has no task URL either. */}
                          {(d.url || d.turl)
                            ? <a href={d.url || d.turl} target="_blank" rel="noreferrer" className="pp-doc-link">{d.k}</a>
                            : <span className="pp-name">{d.k}</span>}
                          {d.n && <span className="pp-uid">{d.n}</span>}
                        </td>
                        {/* A formal training has no revision axis (Critical Note #26) — a dash,
                            not a blank, so it reads as "does not apply" and not as missing. */}
                        <td>{d.rev || <span className="pp-dash" title="A formal training record has no revision.">—</span>}</td>
                        <td><span className={`pp-pill ${st.cls}`}>{st.label}</span></td>
                        <td>{d.sd || <span className="pp-zero">—</span>}</td>
                        <td>{d.due || <span className="pp-zero">—</span>}</td>
                        <td className="pp-role">{d.panel || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {!p.no_obligations && (
              <button
                type="button"
                className="pp-cert-btn pp-drawer-cert"
                disabled={certBusy === p.uid}
                onClick={() => onCertificate(p.uid)}
              >
                {certBusy === p.uid ? '…' : '📜 Certificate'}
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

/** Progress, or the one state a percentage cannot express. */
function Progress({ p }) {
  if (p.no_obligations) {
    return (
      <span
        className="pp-pill pp-na"
        title="No controlled document names this person's job title, and no formal training lists them. They owe nothing — which is a finding about the library's configuration, not a compliance verdict about the person."
      >
        no training assigned
      </span>
    );
  }
  return (
    <div className="pp-meter" title={`${p.done} signed of ${p.total} required`}>
      <div className="pp-meter-track">
        <i style={{ width: `${p.pct}%`, background: pctColor(p.pct) }} />
      </div>
      {/* The denominator always travels with the percentage — a bare number is not auditable,
          the same rule the certificate prints by. */}
      <span className="pp-meter-pc">{p.pct}%</span>
      <span className="pp-meter-of">{p.done}/{p.total}</span>
    </div>
  );
}

function PeoplePanel() {
  const navigate = useNavigate();
  const { setupId } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [state, setState] = useState('all');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');       // the debounced term actually sent
  const [cert, setCert] = useState(null);       // { person, builtAt }
  const [certBusy, setCertBusy] = useState('');
  // The open training-file drawer: { uid, name, role, only, loading, error, person, builtAt }
  const [file, setFile] = useState(null);

  // Typing is debounced so a search does not re-aggregate every snapshot per keystroke —
  // the merge is cheap (no Orcanos, no I/O) but it is not free.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const token = ++reqRef.current;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page), limit: String(pageSize), state, q: query,
      });
      const r = await fetch(`/api/people/${setupId}?${params}`, { credentials: 'include' });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `Could not read this panel (${r.status})`);
      // An answer to a query the user has already moved past must never overwrite a newer one.
      if (reqRef.current === token) setData(body);
    } catch (err) {
      if (reqRef.current === token) setError(err.message);
    } finally {
      if (reqRef.current === token) setLoading(false);
    }
  }, [setupId, page, pageSize, state, query]);

  useEffect(() => { load(); }, [load]);

  /** One person's training file, as a drawer.
   *
   * It reads the SAME endpoint the certificate does (`/person/{uid}`) — the roster page omits
   * `docs` deliberately, and a second endpoint returning the same rows a different way is how
   * a drawer starts disagreeing with the row that opened it. `only` narrows the view; it never
   * changes what was fetched, so the counts in the drawer's own header stay the person's real
   * totals rather than the filtered ones.
   */
  const openFile = useCallback(async (person, only = 'all') => {
    setFile({ uid: person.uid, name: person.name || person.uid, role: person.role,
              only, loading: true, error: '', person: null });
    try {
      const r = await fetch(`/api/people/${setupId}/person/${encodeURIComponent(person.uid)}`,
        { credentials: 'include' });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || 'Could not read this person');
      setFile((f) => (f && f.uid === person.uid
        ? { ...f, loading: false, person: body.person, builtAt: body.built_at }
        : f));
    } catch (err) {
      setFile((f) => (f && f.uid === person.uid ? { ...f, loading: false, error: err.message } : f));
    }
  }, [setupId]);

  /** The certificate needs the person's full training file, which the roster page omits. */
  const openCertificate = useCallback(async (uid) => {
    setCertBusy(uid);
    try {
      const r = await fetch(`/api/people/${setupId}/person/${encodeURIComponent(uid)}`,
        { credentials: 'include' });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || 'Could not read this person');
      setCert({ person: body.person, builtAt: body.built_at });
    } catch (err) {
      setError(err.message);
    } finally {
      setCertBusy('');
    }
  }, [setupId]);

  const totals = data?.totals;
  const people = data?.people || [];
  const unbuilt = (data?.coverage || []).filter((c) => !c.built);

  return (
    <div className="pp-page">
      <div className="pp-head">
        <button className="pp-back" onClick={() => navigate('/training')}>← Back</button>
        <div className="pp-head-text">
          <h1>{data?.name || 'People'}</h1>
          <p className="pp-sub">
            Everyone in the User Profile filter, with their training standing across every
            training panel in this account. Nothing to configure — it reads the snapshots
            those panels have already built.
          </p>
        </div>
      </div>

      {error && <div className="pp-error">{error}</div>}

      {/* Coverage first, and above the numbers rather than under them: every figure below is
          only as complete as the panels it was drawn from, and a reader has to know that
          BEFORE reading them. */}
      {totals && (
        <div className={`pp-coverage${unbuilt.length ? ' pp-coverage-warn' : ''}`}>
          <span className="pp-cov-main">
            Built from <b>{totals.panels_built}</b> of <b>{totals.panels}</b> training
            {totals.panels === 1 ? ' panel' : ' panels'}
          </span>
          <span className="pp-cov-sep">·</span>
          <span
            title="A record drawn from several snapshots is only as current as the oldest one, so that is the date it is stamped with."
          >
            oldest snapshot {formatStamp(totals.built_at)}
          </span>
          {unbuilt.length > 0 && (
            <span className="pp-cov-warn">
              ⚠ Never built, so nothing they cover is counted here:{' '}
              {unbuilt.map((c) => c.name).join(', ')}
            </span>
          )}
        </div>
      )}

      {(data?.problems || []).map((p) => (
        <div className="pp-note" key={p}>{p}</div>
      ))}

      {totals && (
        <div className="pp-kpis">
          <div className="pp-kpi"><b>{totals.people}</b><span>People</span></div>
          <div className="pp-kpi ok"><b>{totals.compliant}</b><span>Fully signed</span></div>
          <div className="pp-kpi open"><b>{totals.outstanding}</b><span>Outstanding trainings</span></div>
          <div className="pp-kpi miss">
            <b>{totals.overdue === null ? '—' : totals.overdue}</b>
            <span title={totals.overdue === null
              ? 'The due dates in these snapshots read the same either way round, so no overdue count is shown rather than a guessed one.'
              : undefined}
            >Overdue</span>
          </div>
          {/* Its own tile, never folded into a percentage: these people owe nothing because
              no document names their job title, which is a configuration finding. */}
          <div className="pp-kpi na"><b>{totals.no_obligations}</b><span>No training assigned</span></div>
        </div>
      )}

      <div className="pp-toolbar">
        <input
          className="pp-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, user id or job title…"
          aria-label="Search people"
        />
        <div className="pp-chips" role="group" aria-label="Filter people">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`pp-chip${state === f.key ? ' active' : ''}`}
              onClick={() => { setState(f.key); setPage(1); }}
              aria-pressed={state === f.key}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pp-card">
        <table className="pp-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Job title</th>
              <th className="num">Signed</th>
              <th className="num">Open</th>
              <th className="num">No task</th>
              <th className="num">Overdue</th>
              <th className="pp-col-progress">Total progress</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {people.map((p) => (
              /* The whole row opens this person's training file. A roster row raises exactly
                 one question — "which ones?" — and until now the only way to answer it was to
                 print a certificate, which is a document, not a way of looking. */
              <tr
                key={p.uid}
                className={p.no_obligations ? '' : 'pp-clickable'}
                role={p.no_obligations ? undefined : 'button'}
                tabIndex={p.no_obligations ? undefined : 0}
                title={p.no_obligations ? undefined : 'Open this person’s training file'}
                onClick={p.no_obligations ? undefined : () => openFile(p)}
                onKeyDown={p.no_obligations ? undefined : (e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFile(p); }
                }}
              >
                <td>
                  <div className="pp-who">
                    <span className="pp-avatar">{initials(p.name, p.uid)}</span>
                    <span>
                      {/* Printed exactly as the tenant renders it. A USRP Name is one string
                          (`Quincy.Angel`, `Orcanos. Tech` are real values) — splitting it into
                          first and last would be a guess on a compliance screen. */}
                      <span className="pp-name">{p.name || p.uid}</span>
                      <span className="pp-uid">{p.uid}</span>
                    </span>
                  </div>
                </td>
                <td className="pp-role">{p.role || <span className="pp-dash" title="This person's User Profile carries no Job Title, so no document's Roles field can ever name them.">not set</span>}</td>
                {/* A count opens the file already narrowed to what it counted — the question a
                    number raises is which rows it is made of, and answering it with the whole
                    file makes the reader filter by eye. A zero opens nothing. */}
                <td className="num">
                  {p.done
                    ? <button type="button" className="pp-num-btn"
                              onClick={(e) => { e.stopPropagation(); openFile(p, 'done'); }}>{p.done}</button>
                    : <span className="pp-zero">0</span>}
                </td>
                <td className="num">
                  {(p.open + p.draft)
                    ? <button type="button" className="pp-num-btn"
                              onClick={(e) => { e.stopPropagation(); openFile(p, 'open'); }}>{p.open + p.draft}</button>
                    : <span className="pp-zero">0</span>}
                </td>
                <td className="num">
                  {p.miss
                    ? <button type="button" className="pp-pill pp-miss pp-num-btn"
                              onClick={(e) => { e.stopPropagation(); openFile(p, 'miss'); }}>{p.miss}</button>
                    : <span className="pp-zero">0</span>}
                </td>
                <td className="num">
                  {p.overdue === null
                    ? <span className="pp-dash" title="Due dates could not be read from these snapshots.">—</span>
                    : p.overdue
                      ? <span className="pp-pill pp-miss" title={p.oldest_due ? `Oldest outstanding due ${p.oldest_due}` : undefined}>{p.overdue}</span>
                      : <span className="pp-zero">0</span>}
                </td>
                <td><Progress p={p} /></td>
                <td className="pp-actions">
                  {/* A certificate is a record of evidence. Somebody with no obligations has
                      none to record, so the button is not offered — a sheet headed with their
                      name and an empty table reads as a clean bill of health. */}
                  {!p.no_obligations && (
                    <button
                      type="button"
                      className="pp-cert-btn"
                      disabled={certBusy === p.uid}
                      onClick={(e) => { e.stopPropagation(); openCertificate(p.uid); }}
                      title="Printable training compliance certificate for this person, across every panel"
                    >
                      {certBusy === p.uid ? '…' : '📜 Certificate'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && people.length === 0 && (
              <tr>
                <td colSpan={8} className="pp-empty">
                  {/* An empty filter and an empty panel are opposite findings. */}
                  {data && data.total_unfiltered > 0
                    ? 'Nobody matches this search or filter.'
                    : totals && totals.panels_built === 0
                      ? 'No training panel in this account has been built yet — open one and build its report, and everyone it covers appears here.'
                      : 'No user profiles came back from the training panels in this account.'}
                </td>
              </tr>
            )}
            {loading && (
              <tr><td colSpan={8} className="pp-empty">Reading snapshots…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > 0 && (
        <div className="pp-foot">
          <select
            className="pp-rows"
            value={pageSize}
            onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
            aria-label="Rows per page"
          >
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} per page</option>)}
          </select>
          <button disabled={data.page <= 1} onClick={() => setPage(data.page - 1)}>← Prev</button>
          <span className="pp-pageno">Page {data.page} of {data.pages}</span>
          <button disabled={data.page >= data.pages} onClick={() => setPage(data.page + 1)}>Next →</button>
          <span className="pp-showing">{data.total} {data.total === 1 ? 'person' : 'people'}</span>
        </div>
      )}

      {file && (
        <TrainingFile
          file={file}
          certBusy={certBusy}
          onClose={() => setFile(null)}
          onCertificate={openCertificate}
        />
      )}

      {cert && (
        <TrainingCertificate
          person={cert.person}
          /* A training panel id, not this one: the profile item and the record sign-off are
             read through the training routes, and a People panel has no filters of its own. */
          setupId={cert.person.setup_id}
          panelName={`${data?.name || 'People'} · ${(cert.person.panels || []).join(', ')}`}
          builtAt={cert.builtAt}
          onClose={() => setCert(null)}
        />
      )}
    </div>
  );
}

export default PeoplePanel;
